import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import BackHeader from "../components/BackHeader";
import PopupBlockedModal from "../components/PopupBlockedModal";
import apiClient from "../api/config";
import {
  SupportedCurrency,
  formatPrice as formatPriceWithCurrency,
  getCurrencyFromLanguage,
  getCurrencyForCart,
  convertCurrency,
} from "../utils/currencyUtils";
import { getCurrentLanguage, SupportedLanguage, LANGUAGE_CHANGE_EVENT } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { useAddress } from "../contexts/AddressContext";
import AddressLines from './address/components/AddressLines';
import { useDisplayAddress } from '../hooks/useDisplayAddress';
import { CartResponse, calculateOrder, calculateOrderGift, transformCartToCalculateRequest, transformCartToCalculateGiftRequest, CalculateOrderResponse } from "../api/cartService";
import { prepareEximbayPayment, getOrderStatus, verifyEximbayPayment as confirmEximbayPayment } from "../api/paymentService";
import { getEximbayConfig, getReturnUrl, getStatusUrl } from "../config/eximbayConfig";
import { EximbayPaymentParams, EximbayProductInfo } from "../types/payment";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { isPayPalDisabled } from "../config/paymentConfig";
import { getAssetPath } from "../utils/assetPath";
import { getPromotionUserId } from "../utils/promotionUserIdUtils";
import { getPromotionResolution } from "../utils/promotionResolutionUtils";
import { trackEvent } from "../services/AnalyticsService";
import { POPUP_SETTINGS_GUIDE_URL } from "../config/constants";

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { cart, fetchOrderId } = useCart();
  const { address } = useAddress();
  const displayAddress = useDisplayAddress(address);
  const { t } = useTranslation();

  // Get currency and orderId from URL parameters
  const location = window.location;
  const urlParams = new URLSearchParams(location.search);
  const currencyParam = urlParams.get("currency");
  const urlOrderId = urlParams.get("orderId"); // URL에서 orderId 가져오기

  // Email state management
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(false);

  // Terms expansion state
  const [termsExpanded, setTermsExpanded] = useState(false);

  // Payment processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  // 엑심베이 결제 검증 상태 (백그라운드 처리용)
  const [isVerifying, setIsVerifying] = useState(false);
  
  // 팝업 블록 감지 상태
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  
  // 팝업 활동 감지 플래그 (엑심베이로부터 메시지를 받았는지 추적)
  const popupActivityDetected = useRef(false);

  
  // OrderId 상태
  const [cartOrderId, setCartOrderId] = useState<string | null>(null);
  
  // CartResponse 데이터 상태
  const [cartResponse, setCartResponse] = useState<CartResponse | null>(null);

  // 주문 금액 계산 상태 추가
  const [orderCalculation, setOrderCalculation] = useState<CalculateOrderResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  // 현재 언어 가져오기
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getCurrentLanguage());

  // PayPal 비활성화 여부 확인
  const payPalDisabled = useMemo(() => isPayPalDisabled(), []);

  // Currency selection state - use URL parameter if available, else based on current language
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    (currencyParam as SupportedCurrency) || getCurrencyForCart(currentLanguage),
  );

  // 기프트샵 여부 확인
  const isGiftShop = useMemo(() => {
    return cart?.restaurantId === 'gift-shop-1';
  }, [cart?.restaurantId]);

  // 언어 변경 감지 및 업데이트
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.language) {
        const newLanguage = customEvent.detail.language as SupportedLanguage;
        setCurrentLanguage(newLanguage);
        // URL에서 통화를 지정하지 않은 경우 언어에 따라 통화 업데이트
        if (!currencyParam) {
          setSelectedCurrency(getCurrencyForCart(newLanguage));
        }
        
        // 언어 변경 시 계산 데이터만 초기화 (cartOrderId는 유지)
        setOrderCalculation(null);
      }
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [currencyParam]);

  // 주소 미설정 시 주소 설정 페이지로 리다이렉트 (기존 CartPage 패턴과 동일)
  useEffect(() => {
    if (!address?.isComplete) {
      navigate(
        { pathname: "/address", search: routerLocation.search },
        { replace: true }
      );
    }
  }, [address, navigate, routerLocation.search]);

  // 주문 금액 계산 API 호출 - currentLanguage 의존성 제거
  useEffect(() => {
    const calculateOrderAmount = async () => {
      if (!cart || !cart.items.length || !address?.addressId) {
        return;
      }

      try {
        setIsCalculating(true);
        const promotionUserId = getPromotionUserId();
        // 기프트샵 여부에 따라 다른 변환 함수와 API 호출
        const calculation = isGiftShop 
          ? await calculateOrderGift(transformCartToCalculateGiftRequest(
              cart, 
              address.addressId, 
              currentLanguage.toUpperCase()
            ), promotionUserId)
          : await calculateOrder(transformCartToCalculateRequest(
              cart, 
              address.addressId, 
              currentLanguage.toUpperCase()
            ), promotionUserId);
        
        setOrderCalculation(calculation);
        
      } catch (error) {
        console.error('[CheckoutPage] 주문 금액 계산 실패:', error);
        setPaymentError("주문 금액 계산에 실패했습니다.");
      } finally {
        setIsCalculating(false);
      }
    };

    calculateOrderAmount();
  }, [cart, address?.addressId, isGiftShop, currentLanguage]); // currentLanguage 의존성 추가

  // 주소가 있을 때 orderId 가져오기 - fetchOrderId 의존성 제거 및 최적화
  useEffect(() => {
    const getOrderId = async () => {
      // URL에 orderId가 있으면 그것을 사용
      if (urlOrderId && !cartOrderId) {
        setCartOrderId(urlOrderId);
        return;
      }
      
      // 이미 cartOrderId가 있으면 추가 호출하지 않음
      if (cart && address?.addressId && !cartOrderId && !cartResponse) {
        try {
          const originStayId = address.stay?.stay_id ?? address.stay?.id;
          const cartResponseData = await fetchOrderId(address.addressId, undefined, originStayId);
          if (cartResponseData && cartResponseData.orderId) {
            setCartOrderId(cartResponseData.orderId);
            setCartResponse(cartResponseData);
          }
        } catch (error) {
          console.error("Error fetching order ID:", error);
        }
      }
    };

    getOrderId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, address?.addressId, cartOrderId, cartResponse, urlOrderId]); // fetchOrderId는 stable하므로 의존성에서 제외

  // PayPal initial options
  const getPayPalClientId = () => {
    // 환경변수에서 Client ID 가져오기
    if (process.env.REACT_APP_PAYPAL_CLIENT_ID) {
      return process.env.REACT_APP_PAYPAL_CLIENT_ID;
    }

    // 운영 환경 감지 (GitHub Pages + koreaorder.com)
    const isProduction =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("github.io") ||
       window.location.hostname.includes("koreaorder.com"));

    // 환경에 따른 Client ID 설정
    return isProduction
      ? "Afv61ACQRsepyvxakr_qie_QpEo6WxPo8ZFrGUT12inp-0aEWQQoAwLbAnv5JEzY77tBRZLNekpfFjT5" // live 운영용
      : "AU2Wemo0OEBAvigZUoTIgwbtE9S6Tb6VzZxwmFPmBdUr9wGUNUrCZ9tZiuEFv3zheZmlnkG9yGb9Kj64"; // sandbox 개발용
  };

  const paypalOptions = {
    clientId: getPayPalClientId(),
    currency: selectedCurrency,
    intent: "capture",
    locale: "en_US",
  };

  // Subtotal (cart items total) - API 응답값만 사용
  const subtotal = useMemo(() => {
    if (!orderCalculation) return 0;

    // 소계는 프로모션 영향을 받지 않음 (배달비 프로모션)
    switch(selectedCurrency) {
      case 'USD': return orderCalculation.subtotalUsd;
      case 'JPY': return orderCalculation.subtotalJpy;
      case 'CNY': return orderCalculation.subtotalCny;
      case 'TWD': return orderCalculation.subtotalTwd;
      case 'KRW':
      default: return orderCalculation.subtotalKrw;
    }
  }, [orderCalculation, selectedCurrency, currentLanguage]);

  // 프로모션 활성화 여부 확인
  const isPromotionActive = useMemo(() => {
    if (!orderCalculation) return false;

    switch(selectedCurrency) {
      case 'USD': return orderCalculation.isPromotionUsd;
      case 'JPY': return orderCalculation.isPromotionJpy;
      case 'CNY': return orderCalculation.isPromotionCny;
      case 'TWD': return orderCalculation.isPromotionTwd;
      case 'KRW':
      default: return false; // KRW는 프로모션 필드가 없으므로 false
    }
  }, [orderCalculation, selectedCurrency, currentLanguage]);
                       
  // 배송비 - API 응답값만 사용
  const DELIVERY_FEE = useMemo(() => {
    if (!orderCalculation) return 0;
    
    // 프로모션 활성화 여부에 따라 할인된 배송비 또는 원래 배송비 반환
    if (isPromotionActive) {
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.promotionDeliveriesUsd;
        case 'JPY': return orderCalculation.promotionDeliveriesJpy;
        case 'CNY': return orderCalculation.promotionDeliveriesCny;
        case 'TWD': return orderCalculation.promotionDeliveriesTwd;
        case 'KRW':
        default: return orderCalculation.deliveryFeeKrw; // KRW는 프로모션 배달비 필드가 없으므로 원래 배달비 사용
      }
    } else {
      // 프로모션이 비활성화된 경우 원래 배송비
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.deliveryFeeUsd;
        case 'JPY': return orderCalculation.deliveryFeeJpy;
        case 'CNY': return orderCalculation.deliveryFeeCny;
        case 'TWD': return orderCalculation.deliveryFeeTwd;
        case 'KRW':
        default: return orderCalculation.deliveryFeeKrw;
      }
    }
  }, [orderCalculation, selectedCurrency, isPromotionActive, currentLanguage]);

  // 원래 배달비 (프로모션 적용 전)
  const originalDeliveryFee = useMemo(() => {
    if (!orderCalculation) return 0;

    switch(selectedCurrency) {
      case 'USD': return orderCalculation.deliveryFeeUsd;
      case 'JPY': return orderCalculation.deliveryFeeJpy;
      case 'CNY': return orderCalculation.deliveryFeeCny;
      case 'TWD': return orderCalculation.deliveryFeeTwd;
      case 'KRW':
      default: return orderCalculation.deliveryFeeKrw;
    }
  }, [orderCalculation, selectedCurrency, currentLanguage]);

  // 프로모션 할인율 계산 (배달비 기준)
  const promotionDiscountRate = useMemo(() => {
    if (!orderCalculation || !isPromotionActive) return 0;
    
    const promotionRatio = orderCalculation.promotionRatio || 0;
    return Math.round(promotionRatio * 100); // 백분율로 변환
  }, [orderCalculation, isPromotionActive]);

  // Total order amount - API 응답값만 사용
  const total = useMemo(() => {
    if (!orderCalculation) return 0;
    
    // 프로모션 활성화 여부 확인 및 프로모션 총액 사용
    if (isPromotionActive) {
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.promotionTotalPriceUsd;
        case 'JPY': return orderCalculation.promotionTotalPriceJpy;
        case 'CNY': return orderCalculation.promotionTotalPriceCny;
        case 'TWD': return orderCalculation.promotionTotalPriceTwd;
        case 'KRW':
        default: return orderCalculation.totalPriceKrw; // KRW는 프로모션 총액 필드가 없으므로 원래 총액 사용
      }
    } else {
      // 프로모션이 비활성화된 경우 원래 총액
      switch(selectedCurrency) {
        case 'USD': return orderCalculation.totalPriceUsd;
        case 'JPY': return orderCalculation.totalPriceJpy;
        case 'CNY': return orderCalculation.totalPriceCny;
        case 'TWD': return orderCalculation.totalPriceTwd;
        case 'KRW':
        default: return orderCalculation.totalPriceKrw;
      }
    }
  }, [orderCalculation, selectedCurrency, isPromotionActive, currentLanguage]);

  const promotionResolution = useMemo(
    () => getPromotionResolution(orderCalculation),
    [orderCalculation]
  );

  useEffect(() => {
    if (!orderCalculation) return;
    if (process.env.NODE_ENV !== 'development') return;
    console.info('[CheckoutPage] Promotion resolution', promotionResolution);
  }, [orderCalculation, promotionResolution]);

  // Price formatting with selected currency - 통화별 소수점 처리 조정
  const formatPrice = (price: number) => {
    // 통화별 적절한 소수점 처리
    switch(selectedCurrency) {
      case 'USD':
        return `$${price.toFixed(2)}`;
      case 'JPY':
        return `¥${Math.round(price)}`;
      case 'KRW':
        return `₩${Math.round(price).toLocaleString()}`;
      case 'CNY':
        return `¥${price.toFixed(1)}`;
      case 'TWD':
        return `NT$${Math.round(price)}`;
      default:
        return formatPriceWithCurrency(price, selectedCurrency);
    }
  };

  // Email validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Email input change handler
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailValid(validateEmail(newEmail));
  };

  // Create PayPal order
  const createOrder = (data: any, actions: any) => {
    if (!emailValid || !cart || !orderCalculation) {
      return Promise.reject(new Error("Required data not available"));
    }

    // items 배열 생성 및 실제 item_total 계산
    const paypalItems = cart.items.map((item) => {
      // 현재 통화에 맞는 가격 필드 선택
      const priceField = `price${selectedCurrency.charAt(0).toUpperCase() + selectedCurrency.slice(1).toLowerCase()}` as keyof typeof item;
      // 해당 통화 가격이 있으면 사용, 없으면 기본 가격 사용
      const itemPrice = item[priceField] !== undefined ? Number(item[priceField]) : item.price;
      
      return {
        name: item.name,
        unit_amount: {
          currency_code: selectedCurrency,
          value: itemPrice.toFixed(2),
        },
        quantity: item.quantity,
      };
    });

    // PayPal items 배열의 실제 합계 계산
    const calculatedItemTotal = paypalItems.reduce((sum, item) => {
      return sum + (parseFloat(item.unit_amount.value) * item.quantity);
    }, 0);

    // 정확한 total 계산
    const calculatedTotal = calculatedItemTotal + DELIVERY_FEE;

    return actions.order.create({
      purchase_units: [
        {
          description: `Order from ${cart.restaurantName}`,
          amount: {
            currency_code: selectedCurrency,
            value: calculatedTotal.toFixed(2),
            breakdown: {
              item_total: {
                currency_code: selectedCurrency,
                value: calculatedItemTotal.toFixed(2),
              },
              shipping: {
                currency_code: selectedCurrency,
                value: DELIVERY_FEE.toFixed(2),
              },
            },
          },
          items: paypalItems,
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
      },
    });
  };

  // PayPal payment approval handler
  const onApprove = async (data: any, actions: any) => {
    if (!cart) return;

    setIsProcessing(true);
    setPaymentError(null);

    try {
      await actions.order.capture();

      // Get orderId from cart or from the API call
      const orderId = cartOrderId || cart.orderId;
      
      if (!orderId) {
        throw new Error("Order ID not found. Please try again.");
      }

      // PayPal order ID를 payment ID로 사용
      const paymentId = data.orderID;

      // Prepare backend API request body - 백엔드가 기대하는 단순한 형식으로 수정
      const promotionUserId = getPromotionUserId();
      const requestBody = {
        paypalOrderId: paymentId,
        email: email,
        promotionUserId: promotionUserId || undefined,
      };

      let apiSuccess = false;
      let retryCount = 0;
      const MAX_RETRIES = 1;

      while (!apiSuccess && retryCount <= MAX_RETRIES) {
        try {
          // Call backend API using apiClient
          const response = await apiClient.post(
            `/api/orders/${orderId}/payment`,
            requestBody
          );

          if (response.status >= 200 && response.status < 300) {
            apiSuccess = true;

            // Navigate to success page
            navigate("/order-success", {
              state: {
                orderId: orderId,
                paymentId: paymentId,
                currency: selectedCurrency,
              },
            });
          } else {
            console.error(`API call failed (attempt ${retryCount + 1}):`, response.statusText);
            retryCount++;
            if (retryCount <= MAX_RETRIES) {
            }
          }
        } catch (error) {
          console.error(`API error (attempt ${retryCount + 1}):`, error);
          retryCount++;
          if (retryCount <= MAX_RETRIES) {
          }
        }
      }

      // 모든 시도 후에도 실패한 경우
      if (!apiSuccess) {
        // 실패한 주문 정보를 슬랙으로 전송 - 운영 서버 API 활용
        try {
          await apiClient.post(
            "https://api.brainbackdoor.com/api/notifications/slack/test/order",
            {
              message: `결제 API 호출 실패. orderId: ${orderId}, paymentId: ${paymentId}, 금액: ${formatPrice(total)}, 이메일: ${email}, 시각: ${new Date().toISOString()}`
            }
          );
        } catch (slackError) {
          console.error("Failed to send error notification:", slackError);
        }

        // 사용자에게 오류 메시지 표시
        setPaymentError(
          t('checkout.paymentProcessing')
        );
      }
    } catch (error) {
      console.error("PayPal payment error:", error);
      setPaymentError(
        "An error occurred during payment processing. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // 엑심베이 유틸리티 함수들
  
  // 현재 언어에 맞는 장바구니 아이템 이름 반환
  const getLocalizedItemName = (item: any, language: SupportedLanguage): string => {
    const nameField = `name${language === 'en' ? 'En' : 
                     language === 'ja' ? 'Ja' : 
                     language === 'zh-CN' ? 'ZhCn' : 
                     language === 'zh-TW' ? 'ZhTw' : ''}` as keyof typeof item;
    
    return item[nameField] !== undefined ? String(item[nameField]) : item.name;
  };
  
  // 현재 언어 기준 일반 가격 반환 (프로모션 미적용)
  const getItemRegularPrice = (item: any, language: SupportedLanguage): number => {
    const currency = getCurrencyFromLanguage(language);
    
    // 통화별 일반 가격 필드에서 가격 가져오기 (프로모션 가격 제외)
    switch (currency) {
      case 'USD':
        return item.priceUsd ?? item.price;
      case 'JPY':
        return item.priceJpy ?? item.price;
      case 'CNY':
        return item.priceCny ?? item.price;
      case 'TWD':
        return item.priceTwd ?? item.price;
      case 'KRW':
      default:
        return item.priceKrw ?? item.price;
    }
  };
  
  // 엑심베이 제품 배열 생성
  const buildEximbayProducts = (): EximbayProductInfo[] => {
    if (!cart) return [];
    
    return cart.items.map(item => ({
      name: getLocalizedItemName(item, currentLanguage),
      quantity: item.quantity,
      unit_price: getItemRegularPrice(item, currentLanguage),
      link: window.location.origin
    }));
  };

  // 엑심베이 결제 상태 검증 함수 (백그라운드 처리)
  const verifyEximbayOrderStatus = async (orderId: string) => {
    if (isVerifying) return; // 중복 호출 방지
    
    setIsVerifying(true);
    
    try {
      const orderStatus = await getOrderStatus(orderId);
      const paidStatuses = new Set(['PAID', 'BROKER_ACCEPTED', 'COOKING', 'DELIVERY_COMPLETED']);
      const isPaid = paidStatuses.has(orderStatus.status) || !!orderStatus.paidAt;
      
      if (isPaid) {
        // 주문 상태 기준 결제 완료 확인 → 성공 처리
        console.log('주문 조회 결과: 결제 완료 확인');
        // [트래킹] 백그라운드 검증 결과: 결제 완료
        trackEvent('eximbay_verify_paid', { order_id: orderId });
        navigate("/order-success", {
          state: {
            orderId: orderId,
            paymentId: orderId,
            currency: selectedCurrency,
          },
        });
        setIsProcessing(false);
      } else {
        // 주문 상태 기준 결제 미완료 → 실패 처리 (기존 UI 그대로)
        console.log('주문 조회 결과: 결제 미완료');
        // [트래킹] 백그라운드 검증 결과: 결제 미완료
        trackEvent('eximbay_verify_not_paid', { order_id: orderId, status: orderStatus.status });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('주문 상태 조회 API 오류:', error);
      // [트래킹] 백그라운드 검증 에러
      trackEvent('eximbay_verify_error', { order_id: orderId, error: error instanceof Error ? error.message : String(error) });
      setPaymentError('네트워크 문제로 결제 상태를 확인할 수 없습니다. FAQ > Get Help Now를 통해 문의해 주세요.');
      setIsProcessing(false);
    } finally {
      setIsVerifying(false);
    }
  };


  // Eximbay payment handler
  const handleEximbayPayment = async () => {
    if (!cart) return;
    
    // 중복 호출 방지
    if (isProcessing) {
      console.log('이미 결제 처리 중입니다. 중복 호출 무시됨.');
      return;
    }

    console.log('=== 엑심베이 결제 시작 ===');
    setIsProcessing(true);
    setPaymentError(null);
    
    // 팝업 활동 플래그 초기화 (새로운 결제 시도마다 리셋)
    popupActivityDetected.current = false;

    // [트래킹] Step 1: 엑심베이 버튼 클릭
    trackEvent('eximbay_click', { currency: selectedCurrency, language: currentLanguage, amount: total });

    try {
      // Get orderId from cart or from the API call
      const orderId = cartOrderId || cart.orderId;
      
      if (!orderId) {
        // [트래킹] orderId 없음
        trackEvent('eximbay_no_order_id');
        throw new Error("Order ID not found. Please try again.");
      }

      // 1. 서버에서 fgkey와 실제 사용한 결제 파라미터들 받기
      const response = await prepareEximbayPayment(orderId, {
        buyerName: email.split('@')[0] || 'Customer', // 이메일에서 이름 추출 또는 기본값
        buyerEmail: email, // 사용자가 입력한 실제 이메일
        productLink: window.location.origin,
        amount: total, // 프론트엔드에서 계산한 총액
        currency: selectedCurrency, // 현재 선택된 통화
        language: currentLanguage, // 현재 언어
        returnUrl: getReturnUrl() // HashRouter 경로 포함된 콜백 URL
      });
      
      const { fgkey, paymentParams } = response;

      // [트래킹] Step 2: 결제 준비 API 성공
      trackEvent('eximbay_prepare_success', { order_id: orderId });

      // 2. 엑심베이 SDK가 로드되었는지 확인
      console.log('SDK 로드 상태 확인:');
      console.log('window.EXIMBAY:', window.EXIMBAY);
      console.log('window.EXIMBAY_SDK_LOADED:', window.EXIMBAY_SDK_LOADED);
      
      if (!window.EXIMBAY) {
        // [트래킹] SDK 미로드
        trackEvent('eximbay_sdk_not_loaded', { sdk_flag: String(!!window.EXIMBAY_SDK_LOADED) });
        throw new Error("Eximbay payment system is not loaded. Please refresh the page.");
      }
      
      if (typeof window.EXIMBAY.request_pay !== 'function') {
        // [트래킹] SDK 함수 없음
        trackEvent('eximbay_sdk_no_function', { request_pay_type: typeof window.EXIMBAY.request_pay });
        throw new Error("Eximbay request_pay function is not available.");
      }

      // 3. 결제창 파라미터 구성
      let eximbayPaymentParams: EximbayPaymentParams;
      
      if (paymentParams) {
        // 1순위: 서버에서 받은 파라미터 사용 (currency, language 포함)
        eximbayPaymentParams = {
          fgkey: fgkey,
          payment: {
            transaction_type: 'PAYMENT',
            order_id: paymentParams.order_id,        // 서버에서 처리한 order_id
            currency: paymentParams.currency,        // 서버에서 처리한 currency (selectedCurrency 기반)
            amount: paymentParams.amount,            // 서버에서 처리한 amount
            lang: paymentParams.lang                 // 서버에서 처리한 lang (currentLanguage 기반)
          },
          merchant: {
            mid: paymentParams.mid                   // 서버에서 실제 사용한 mid
          },
          buyer: {
            name: paymentParams.buyer_name,          // 서버에서 실제 사용한 buyer_name
            email: paymentParams.buyer_email         // 서버에서 실제 사용한 buyer_email
          },
          url: {
            return_url: paymentParams.return_url,    // 서버에서 실제 사용한 return_url
            status_url: paymentParams.status_url     // 서버에서 실제 사용한 status_url
          },
          // 프론트엔드에서 추가하는 새로운 필드들
          product: buildEximbayProducts(),           // 장바구니 기반 제품 정보 배열
          call_from_app: 'N',                       // 웹에서 호출
          display_type: 'P',                        // 팝업 형태
          autoclose: 'N',                           // 3DS 인증을 위해 자동 닫기 비활성화
          ostype: 'M'                               // 모바일 타입
        };
      } else {
        // 2순위: 서버에서 paymentParams를 받지 못한 경우 fallback
        console.warn("서버에서 paymentParams를 받지 못했습니다. 프론트엔드 fallback 사용 중...");
        
        // 프론트엔드에서 서버로 전달한 값들(currency, language)을 활용
        const eximbayConfig = getEximbayConfig();
        
        eximbayPaymentParams = {
          fgkey: fgkey,
          payment: {
            transaction_type: 'PAYMENT',
            order_id: orderId,
            currency: selectedCurrency, // 서버로 전달한 currency 값 사용
            amount: total,
            lang: (() => {
              // 서버로 전달한 language 값을 Eximbay 언어 코드로 변환
              switch(currentLanguage) {
                case 'ko': return 'KO';
                case 'zh-CN': return 'CN';
                case 'zh-TW': return 'TW';
                case 'ja': return 'JP';
                case 'en':
                default: return 'EN';
              }
            })()
          },
          merchant: {
            mid: eximbayConfig.mid
          },
          buyer: {
            name: email.split('@')[0] || 'Customer',
            email: email
          },
          url: {
            return_url: getReturnUrl(),
            status_url: getStatusUrl()
          },
          // 프론트엔드에서 추가하는 새로운 필드들
          product: buildEximbayProducts(),           // 장바구니 기반 제품 정보 배열
          call_from_app: 'N',                       // 웹에서 호출
          display_type: 'P',                        // 팝업 형태
          autoclose: 'N',                           // 3DS 인증을 위해 자동 닫기 비활성화
          ostype: 'M'                               // 모바일 타입
        };
      }

      // 4. 결제 완료 메시지 수신을 위한 이벤트 리스너 등록
      const handlePaymentMessage = async (event: MessageEvent) => {
        if (event.data && (event.data.type === 'PAYMENT_SUCCESS' || event.data.type === 'PAYMENT_FAILURE')) {
          // 메시지를 받았다는 것은 팝업이 실제로 열렸다는 증거
          popupActivityDetected.current = true;
          console.log('팝업 활동 감지: 메시지 수신됨');
        }
        
        if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
          console.log('엑심베이 결제 성공 메시지 수신');
          
          // [트래킹] Step 7: 결제 성공 메시지 수신
          trackEvent('eximbay_message_success', { order_id: orderId });
          
          // 모든 타이머 정리
          if (waitingNoticeTimeout) clearTimeout(waitingNoticeTimeout);
          if (finalTimeout) clearTimeout(finalTimeout);
          if (popupBlockCheckTimeout) clearTimeout(popupBlockCheckTimeout);
          isHandled = true;
          
          // 이벤트 리스너 제거
          window.removeEventListener('message', handlePaymentMessage);
          
          const paymentData = typeof event.data.paymentData === 'string' ? event.data.paymentData : '';
          if (!paymentData) {
            console.warn('PAYMENT_SUCCESS 메시지에 paymentData가 없습니다. 주문 상태 조회로 폴백합니다.');
            verifyEximbayOrderStatus(orderId);
            return;
          }

          const promotionUserId = getPromotionUserId();
          try {
            await confirmEximbayPayment(orderId, {
              paymentData,
              email: email || undefined,
              promotionUserId: promotionUserId || undefined,
            });

            // 결제 확정 성공 시 성공 페이지로 이동
            navigate("/order-success", {
              state: {
                orderId: orderId,
                paymentId: orderId,
                currency: selectedCurrency,
              },
            });
            setIsProcessing(false);
          } catch (confirmError) {
            console.error('엑심베이 결제 확정 API 실패:', confirmError);
            // 확정 API 실패 시 주문 상태 조회로 한 번 더 확인
            verifyEximbayOrderStatus(orderId);
          }
        } else if (event.data && event.data.type === 'PAYMENT_FAILURE') {
          console.log('엑심베이 결제 실패 메시지 수신 - 백그라운드 검증 시작');
          console.log('rescode:', event.data.rescode);
          console.log('resmsg:', event.data.resmsg);
          
          // [트래킹] Step 7: 결제 실패 메시지 수신
          trackEvent('eximbay_message_failure', { 
            order_id: orderId, 
            rescode: event.data.rescode, 
            resmsg: event.data.resmsg 
          });
          
          // 모든 타이머 정리
          if (waitingNoticeTimeout) clearTimeout(waitingNoticeTimeout);
          if (finalTimeout) clearTimeout(finalTimeout);
          if (popupBlockCheckTimeout) clearTimeout(popupBlockCheckTimeout);
          isHandled = true;
          
          // 이벤트 리스너 제거
          window.removeEventListener('message', handlePaymentMessage);
          
          // 백그라운드에서 엑심베이 조회로 실제 결제 상태 확인
          verifyEximbayOrderStatus(orderId);
        }
      };
      
      // 메시지 이벤트 리스너 등록
      window.addEventListener('message', handlePaymentMessage);

      // 타이머 관리 (메시지 기반 처리에 집중)
      let waitingNoticeTimeout: NodeJS.Timeout | null = null;
      let finalTimeout: NodeJS.Timeout | null = null;
      let popupBlockCheckTimeout: NodeJS.Timeout | null = null;
      let isHandled = false; // 중복 처리 방지

      // 팝업 블록 감지 (3초 후)
      popupBlockCheckTimeout = setTimeout(() => {
        if (!isHandled) {
          // 팝업 활동이 감지되지 않은 경우에만 블록으로 간주
          // (메시지를 받았다면 팝업이 열렸다는 의미이므로 블록이 아님)
          if (!popupActivityDetected.current) {
            console.log('팝업 블록 감지: 3초 내 응답 없음');
            
            // [트래킹] Step 5: 팝업 차단 감지
            trackEvent('eximbay_popup_blocked', { order_id: orderId });
            
            isHandled = true;
            
            // 모든 타이머 정리
            if (waitingNoticeTimeout) clearTimeout(waitingNoticeTimeout);
            if (finalTimeout) clearTimeout(finalTimeout);
            window.removeEventListener('message', handlePaymentMessage);
            
            // 팝업 블록 모달 표시
            setIsPopupBlocked(true);
            setIsProcessing(false);
          } else {
            console.log('팝업 활동 감지됨: 블록이 아님, 정상 플로우 계속');
          }
        }
      }, 3000); // 5초

      // 3분 후 대기 안내 메시지
      waitingNoticeTimeout = setTimeout(() => {
        if (!isHandled) {
          console.log('결제 진행 중 - 3분 경과 안내');
          // UI에 "잠시만 기다려 주세요" 메시지 표시 (기존 UI 변경 없이)
        }
      }, 30 * 1000);

      // 1분 후 최종 타임아웃
      finalTimeout = setTimeout(() => {
        if (!isHandled) {
          console.log('엑심베이 결제창 최종 타임아웃 - 백그라운드 검증 시작');
          
          // [트래킹] Step 8: 최종 타임아웃 (메시지 미수신)
          trackEvent('eximbay_timeout', { order_id: orderId, popup_activity: String(popupActivityDetected.current) });
          
          isHandled = true;
          
          // 모든 타이머 정리
          if (waitingNoticeTimeout) clearTimeout(waitingNoticeTimeout);
          if (popupBlockCheckTimeout) clearTimeout(popupBlockCheckTimeout);
          window.removeEventListener('message', handlePaymentMessage);
          
          // 엑심베이 조회로 실제 결제 상태 확인
          verifyEximbayOrderStatus(orderId);
        }
      }, 60 * 1000); // 5분


      // 5. 엑심베이 결제창 호출
      console.log('엑심베이 결제창 호출 시도:', eximbayPaymentParams);
      console.log('window.EXIMBAY:', window.EXIMBAY);
      
      if (typeof window.EXIMBAY.request_pay === 'function') {
        console.log('request_pay 함수 호출 중...');
        
        // window.open 모니터링을 위한 원본 함수 백업
        const originalWindowOpen = window.open;
        // window.open 함수를 일시적으로 래핑하여 팝업 열림 감지
        window.open = function(...args) {
          console.log('window.open 호출 감지:', args);
          const popup = originalWindowOpen.apply(window, args);
          
          if (popup && !popup.closed) {
            console.log('팝업이 성공적으로 열림');
            popupActivityDetected.current = true;
            // [트래킹] Step 4: 팝업 정상 오픈
            trackEvent('eximbay_popup_opened', { order_id: orderId });
          } else {
            console.log('팝업이 차단되었거나 열리지 않음');
            // [트래킹] Step 4: 팝업 차단 (window.open 레벨)
            trackEvent('eximbay_popup_open_failed', { order_id: orderId });
          }
          
          // 원본 함수 복원
          window.open = originalWindowOpen;
          return popup;
        };
        
        // 엑심베이 SDK가 자체적으로 팝업을 관리하도록 호출
        window.EXIMBAY.request_pay(eximbayPaymentParams);
        console.log('request_pay 함수 호출 완료');
        
        // [트래킹] Step 3: request_pay 호출 완료
        trackEvent('eximbay_request_pay', { order_id: orderId });
        
        // 짧은 지연 후 window.open이 호출되지 않았다면 원본 함수 복원
        setTimeout(() => {
          if (window.open !== originalWindowOpen) {
            console.log('window.open 복원 (타임아웃)');
            window.open = originalWindowOpen;
          }
        }, 1000);
      } else {
        console.error('window.EXIMBAY.request_pay is not a function:', typeof window.EXIMBAY.request_pay);
        // 결제창 호출 실패 시 모든 타이머와 이벤트 리스너 정리
        isHandled = true;
        if (waitingNoticeTimeout) clearTimeout(waitingNoticeTimeout);
        if (finalTimeout) clearTimeout(finalTimeout);
        if (popupBlockCheckTimeout) clearTimeout(popupBlockCheckTimeout);
        window.removeEventListener('message', handlePaymentMessage);
      }

    } catch (error) {
      console.error("=== 엑심베이 결제 에러 ===");
      console.error("Error details:", error);
      console.error("Error type:", typeof error);
      console.error("Error message:", error instanceof Error ? error.message : String(error));
      
      // [트래킹] 결제 프로세스 에러
      trackEvent('eximbay_error', { 
        error_message: error instanceof Error ? error.message : String(error),
        step: 'handle_payment'
      });
      
      // 에러 발생 시 모든 message 이벤트 리스너 정리는 복잡하므로 생략
      // (실제로는 결제창이 닫히면 자연스럽게 정리됨)
      
      setPaymentError(
        error instanceof Error ? error.message : "An error occurred during payment processing. Please try again."
      );
      setIsProcessing(false);
    }
    // 결제창이 열리면 setIsProcessing(false)는 return_url에서 처리됨
  };

  // Debug: 초기 상태 로그
  useEffect(() => {
    console.log('=== CheckoutPage 초기 상태 ===');
    console.log('termsExpanded:', termsExpanded);
    console.log('emailValid:', emailValid);
    console.log('email:', email);
    console.log('isProcessing:', isProcessing);
    console.log('cart:', cart);
  }, []);

  // Figma MCP: 로컬 캡처 시 결제 UI까지 포함하려면 유효 이메일이 필요함 — figmacapture URL에서만 자동 채움
  useEffect(() => {
    const h = window.location.hostname;
    if (h !== "localhost" && h !== "127.0.0.1") {
      return;
    }
    const params = new URLSearchParams(routerLocation.search);
    if (!params.get("figmacapture")) {
      return;
    }
    if (params.get("figmaskipemail") === "1") {
      return;
    }
    const fixture = params.get("figmafixtureemail") || "figma-capture@example.com";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(fixture)) {
      setEmail(fixture);
      setEmailValid(true);
    }
  }, [routerLocation.search]);

  // Redirect to cart if cart is empty
  if (!cart || cart.items.length === 0) {
    navigate("/cart");
    return null;
  }

  // 계산 중일 때 로딩 표시
  if (isCalculating || !orderCalculation) {
    return (
      <div data-figma-capture-root className="min-h-screen bg-gray-50">
        <BackHeader title={t('cart.checkout') || "Checkout"} />
        <main className="px-4 py-4 pb-32">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4"></div>
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="h-4 bg-gray-200 rounded mb-3"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-figma-capture-root className="min-h-screen bg-gray-50">
      <BackHeader title={t('cart.checkout') || "Checkout"} />

      <main className="px-4 py-4 pb-32">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-gray-800 text-left">
            {t('cart.orderSummary')}
          </h1>
          {/* UI 미노출: 배달 시간 표시 - 예약주문 기능 일시적 미노출 중이므로 항상 "Within 1 hour" 표시 */}
          {/* 배달 시간 표시 */}
          <div className="text-sm text-gray-800 text-right font-bold">
            {/* UI 미노출 중이므로 항상 "Within 1 hour" 표시 (기능 제거가 아님) */}
            {t('restaurant.deliveryTime')}
            
            {/* 예약배달 관련 로직은 유지 (다시 노출 시 대비) */}
            {/* {cartResponse?.reservationDateTime ? (
              (() => {
                // reservationDateTime을 파싱하여 시간 표시
                const reservationDate = new Date(cartResponse.reservationDateTime);
                const hour = reservationDate.getHours();
                const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                const ampm = hour >= 12 ? 'PM' : 'AM';
                return `${hour12}:00 ${ampm} ${t('cart.scheduledDelivery')}`;
              })()
            ) : (
              t('restaurant.deliveryTime') // "1시간 이내"
            )} */}
          </div>
        </div>

        {/* Delivery address section - 읽기 전용 */}
        {address && address.isComplete && (
          <div className="bg-gray-100 rounded-lg px-3 py-2.5 mb-4">
            <AddressLines display={displayAddress} />
          </div>
        )}

        {/* Order information section */}
        <div className="bg-gray-100 rounded-lg p-4 mb-4">
          {/* Restaurant information */}
          <h2 className="text-sm text-gray-500 mb-2 text-left">
            {/* 레스토랑 이름도 다국어 지원 */}
            {(() => {
              // 현재 언어에 맞는 레스토랑 이름 필드
              const restaurantNameField = `restaurantName${currentLanguage === 'en' ? 'En' : 
                                         currentLanguage === 'ja' ? 'Ja' : 
                                         currentLanguage === 'zh-CN' ? 'ZhCn' : 
                                         currentLanguage === 'zh-TW' ? 'ZhTw' : ''}` as keyof typeof cart;
              
              // 현재 언어에 맞는 이름이 있으면 사용
              if (cart[restaurantNameField] !== undefined) {
                return String(cart[restaurantNameField]);
              }
              
              // 영문 이름이 있으면 사용
              if (cart.restaurantNameEn !== undefined) {
                return String(cart.restaurantNameEn);
              }
              
              // 기본 이름 사용
              return cart.restaurantName;
            })()}
          </h2>

          {/* Menu list */}
          <div className="space-y-2 mb-2">
            {cart.items.map((item) => (
              <div key={item.id} className="flex justify-between">
                <div className="flex-1 flex flex-col">
                  <div className="flex items-start">
                    <span className="text-gray-600 mr-2 text-sm">{item.quantity}×</span>
                    <span className="text-gray-800 text-sm">
                      {/* 현재 언어에 맞는 이름 필드가 있다면 사용, 없으면 기본 이름 사용 */}
                      {(() => {
                        // 나중에 다국어 이름 필드가 추가되면 적용할 수 있도록 구조 작성
                        const nameField = `name${currentLanguage === 'en' ? 'En' : 
                                           currentLanguage === 'ja' ? 'Ja' : 
                                           currentLanguage === 'zh-CN' ? 'ZhCn' : 
                                           currentLanguage === 'zh-TW' ? 'ZhTw' : ''}` as keyof typeof item;
                        
                        return item[nameField] !== undefined ? String(item[nameField]) : item.name;
                      })()}
                    </span>
                  </div>
                  {/* UI 미노출: 옵션 아이템 정보 (기본 음료 미제공 등) - 일시적 미노출 */}
                  {/* 옵션 아이템 정보 추가 */}
                  {/* {item.options.length > 0 &&
                    item.options.some((opt) => opt.optionItems.length > 0) && (
                      <div className="ml-6 mt-1 text-xs text-gray-500 text-left">
                        {item.options
                          .filter((option) => option.optionItems.length > 0)
                          .flatMap((option) => option.optionItems)
                          .map((optItem, index, allOptionItems) => (
                            <span key={optItem.id} className="inline">
                              {optItem.name}
                              {index < allOptionItems.length - 1 ? ", " : ""}
                            </span>
                          ))}
                      </div>
                    )} */}
                </div>
                <span className="text-gray-800 font-medium ml-4 text-sm">
                  {formatPrice(
                    (() => {
                      // 현재 통화에 맞는 가격 필드 선택
                      const priceField = `price${selectedCurrency.charAt(0).toUpperCase() + selectedCurrency.slice(1).toLowerCase()}` as keyof typeof item;
                      // 해당 통화 가격이 있으면 사용, 없으면 기본 가격 사용
                      const itemPrice = item[priceField] !== undefined ? Number(item[priceField]) : item.price;
                      return itemPrice * item.quantity;
                    })()
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment summary information */}
        <div
          className="bg-gray-100 rounded-lg p-4 mb-6"
          data-promotion-source={promotionResolution.source}
          data-applied-promotion-id={promotionResolution.appliedPromotionId || ''}
          data-applied-policy-id={promotionResolution.appliedPolicyId || ''}
        >
          <div className="flex justify-between items-center mb-2">
            <p className="text-gray-700 text-sm">{t('cart.subtotal')}</p>
            {currentLanguage === 'zh-CN' ? (
              <div className="text-right">
                <p className="text-gray-600 text-sm">
                  {formatPriceWithCurrency(convertCurrency(subtotal, 'USD', 'CNY'), 'CNY')}
                </p>
              </div>
            ) : (
              <p className="text-gray-700 text-sm">{formatPrice(subtotal)}</p>
            )}
          </div>

          <div className="flex justify-between items-center mb-2">
            <p className="text-gray-700 text-sm">{t('cart.deliveryFee')}</p>
            <div className="text-right">
              {isPromotionActive && (
                <p className="text-xs text-gray-400 line-through">
                  {currentLanguage === 'zh-CN' ? (
                    <div className="space-y-1">
                      <div>{formatPriceWithCurrency(convertCurrency(originalDeliveryFee, 'USD', 'CNY'), 'CNY')}</div>
                    </div>
                  ) : (
                    formatPrice(originalDeliveryFee)
                  )}
                </p>
              )}
              <div className="flex items-center justify-end space-x-2">
                {isPromotionActive && promotionDiscountRate > 0 && (
                  <span className="text-red-500 font-bold text-xs">
                    {promotionDiscountRate === 100 ? `${t('common.free')}` : `${promotionDiscountRate}%`}
                  </span>
                )}
                {currentLanguage === 'zh-CN' ? (
                  <div className="text-right">
                    <p className="font-medium text-gray-800 text-sm">
                      {formatPriceWithCurrency(convertCurrency(DELIVERY_FEE, 'USD', 'CNY'), 'CNY')}
                    </p>
                  </div>
                ) : (
                  <p className="font-medium text-gray-800 text-sm">
                    {formatPrice(DELIVERY_FEE)}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-300 pt-3 flex justify-between items-center">
            <p className="font-medium text-base text-gray-900">{t('cart.total')}</p>
            {currentLanguage === 'zh-CN' ? (
              <div className="text-right">
                <p className="text-gray-600 text-sm">
                  {formatPriceWithCurrency(convertCurrency(total, 'USD', 'CNY'), 'CNY')}
                </p>
                <p className="font-semibold text-base text-gray-900">
                  ≈ {formatPrice(total)}
                </p>
              </div>
            ) : (
              <p className="font-semibold text-base text-gray-900">
                {formatPrice(total)}
              </p>
            )}
          </div>
        </div>

        {/* UI 미노출: 중국어 사용자를 위한 환율 고지 - 일시적 미노출 중 (코드 유지) */}
        {/* {currentLanguage === 'zh-CN' && orderCalculation && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-6 text-center">
            <p className="text-gray-600 text-sm">
              * 汇率参考：¥1 ≈ ${orderCalculation.exchangeRateUsd && orderCalculation.exchangeRateCny ? (orderCalculation.exchangeRateUsd / orderCalculation.exchangeRateCny).toFixed(2) : '0.14'}（每日更新一次）
            </p>
          </div>
        )} */}

        {/* Email input section */}
        <div className="mb-6">
          <label
            htmlFor="email"
            className="block text-gray-800 font-medium mb-2 text-left"
          >
            {t('checkout.email')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={t('checkout.enterEmail')}
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
              email && !emailValid
                ? "border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:ring-blue-200"
            }`}
          />
          {email && !emailValid && (
            <p className="mt-1 text-sm text-red-500 text-left">
              {t('checkout.invalidEmail')}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500 text-left">
            {t('checkout.confirmationEmail')}
          </p>
        </div>

        {/* Payment method section */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">
            {t('checkout.paymentMethod')}
          </h2>

          {/* PayPal 일시 중단 안내 배너 */}
          {payPalDisabled && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-blue-800 font-medium mb-1">
                    {t('checkout.paypalUnavailable.title')}
                  </p>
                  <p className="text-blue-700 text-sm">
                    {t('checkout.paypalUnavailable.message')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Display payment error if any */}
          {paymentError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
              {paymentError}
            </div>
          )}

          {/* Payment buttons section */}
          {emailValid ? (
            <div>
              {/* Eximbay button - PayPal 비활성화 시 상단으로 이동 및 강조 */}
              {payPalDisabled && (
                <div className="mt-4 relative">
                  <div className="absolute -top-2 right-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded z-10">
                    {t('checkout.recommendedPayment')}
                  </div>
                  <button
                    onClick={() => {
                      console.log('엑심베이 버튼 클릭됨!');
                      console.log('emailValid:', emailValid);
                      console.log('isProcessing:', isProcessing);
                      handleEximbayPayment();
                    }}
                    disabled={isProcessing || !emailValid}
                    className={`w-full py-4 rounded-lg font-medium transition-colors ${
                      isProcessing || !emailValid
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-600 inline"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <div className="flex items-center justify-center gap-2 sm:gap-3">
                        <span className="font-bold text-gray-800 text-base sm:text-lg">{t('checkout.otherPaymentOptions')}</span>
                        <img src={getAssetPath('/assets/logos/alipay.svg')} alt="Alipay+" className="h-5 sm:h-6" />
                        <img src={getAssetPath('/assets/logos/wechatpay.svg')} alt="WeChat Pay" className="h-5 sm:h-6" />
                      </div>
                    )}
                  </button>
                  <div className="mt-2 mb-4 text-center">
                    <p className="flex items-start justify-center gap-1 text-gray-500 text-[11px]">
                      <svg className="w-3 h-3 flex-shrink-0 mt-px" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span>{t('checkout.otherPaymentSubtitle')}</span>
                    </p>
                    <a
                      href={POPUP_SETTINGS_GUIDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs font-medium text-red-600 underline underline-offset-2 hover:text-red-700"
                    >
                      {t('checkout.popupGuideLink')}
                    </a>
                  </div>
                </div>
              )}

              {/* 구분선 추가 - PayPal 비활성화 시에만 표시 */}
              {payPalDisabled && (
                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-3 text-gray-500 text-sm">or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
              )}

              {/* PayPal button */}
              <div className={`mt-4 ${payPalDisabled ? 'relative' : ''}`}>
                {payPalDisabled && (
                  <div className="absolute inset-0 bg-white bg-opacity-60 rounded-lg flex items-center justify-center cursor-not-allowed z-10">
                    <span className="text-gray-500 font-medium text-sm">
                      {t('checkout.paypalUnavailable.title')}
                    </span>
                  </div>
                )}
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    style={{ layout: "vertical" }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    disabled={isProcessing || payPalDisabled}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      setPaymentError(
                        "An error occurred with PayPal. Please try again.",
                      );
                    }}
                  />
                </PayPalScriptProvider>

                {isProcessing && (
                  <div className="mt-3 text-center text-gray-600">
                    <p>Processing your payment. Please wait...</p>
                  </div>
                )}
              </div>

              {/* 구분선 추가 - PayPal 비활성화가 아닐 때만 표시 */}
              {!payPalDisabled && (
                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-3 text-gray-500 text-sm">or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
              )}

              {/* Eximbay button - PayPal 비활성화가 아닐 때 기존 위치 */}
              {!payPalDisabled && (
                <div className="mt-4">
                  <button
                    onClick={() => {
                      console.log('엑심베이 버튼 클릭됨!');
                      console.log('emailValid:', emailValid);
                      console.log('isProcessing:', isProcessing);
                      handleEximbayPayment();
                    }}
                    disabled={isProcessing || !emailValid}
                    className={`w-full py-4 rounded-lg font-medium transition-colors ${
                      isProcessing || !emailValid
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-600 inline"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <div className="flex items-center justify-center gap-2 sm:gap-3">
                        <span className="font-bold text-gray-800 text-base sm:text-lg">{t('checkout.otherPaymentOptions')}</span>
                        <img src={getAssetPath('/assets/logos/alipay.svg')} alt="Alipay+" className="h-5 sm:h-6" />
                        <img src={getAssetPath('/assets/logos/wechatpay.svg')} alt="WeChat Pay" className="h-5 sm:h-6" />
                      </div>
                    )}
                  </button>
                  <div className="mt-2 mb-4 text-center">
                    <p className="flex items-start justify-center gap-1 text-gray-500 text-[11px]">
                      <svg className="w-3 h-3 flex-shrink-0 mt-px" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span>{t('checkout.otherPaymentSubtitle')}</span>
                    </p>
                    <a
                      href={POPUP_SETTINGS_GUIDE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs font-medium text-red-600 underline underline-offset-2 hover:text-red-700"
                    >
                      {t('checkout.popupGuideLink')}
                    </a>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-center">
              <p className="text-yellow-700">
                Please enter a valid email address to access the payment
                button.
              </p>
            </div>
          )}
        </div>

        {/* Terms section */}
        <div className="mt-4 text-center text-gray-500 text-sm">
          <p className="mb-2">
            By proceeding with payment,
            <button
              type="button"
              className="text-gray-700 font-medium inline-flex items-center ml-1"
              onClick={() => {
                console.log('Terms 토글 클릭됨! 현재 상태:', termsExpanded);
                console.log('이메일 유효성:', emailValid);
                console.log('이메일 값:', email);
                setTermsExpanded(!termsExpanded);
              }}
            >
              you agree to our Privacy Policy and Terms
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`w-5 h-5 ml-1 transition-transform ${termsExpanded ? "rotate-180" : ""}`}
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
          </p>

          {termsExpanded && (
            <div className="mt-3 text-left bg-gray-50 p-4 rounded-lg border border-gray-200 text-xs leading-relaxed">
              <h3 className="font-bold mb-2 text-gray-700">
                Privacy Policy and Terms of Service
              </h3>
              <p className="mb-2">
                <strong>1. Collection of Personal Information:</strong> We
                collect personal information such as your name, email address,
                phone number, and delivery address to process your order and
                provide you with a seamless food delivery experience.
              </p>
              <p className="mb-2">
                <strong>2. Use of Information:</strong> The information we
                collect is used to process your orders, manage your account,
                provide customer support, and improve our services.
              </p>
              <p className="mb-2">
                <strong>3. Payment Information:</strong> Your payment
                information is processed securely through our payment partners.
                We do not store your full credit card details on our servers.
              </p>
              <p className="mb-2">
                <strong>4. Refund Policy:</strong> Refunds are processed
                according to our refund policy, which may vary depending on the
                restaurant and the circumstances of the refund request.
              </p>
              <p className="mb-2">
                <strong>5. Delivery Terms:</strong> Delivery times are estimates
                and may vary depending on factors such as traffic, weather
                conditions, and restaurant preparation times.
              </p>
              <p className="mb-2">
                <strong>6. Multiple Currencies:</strong> While we display prices
                in multiple currencies for your convenience, the final charges
                may be subject to exchange rates at the time of processing.
              </p>
              <p className="mb-4">
                By proceeding with payment, you acknowledge that you have read
                and agree to these terms and conditions.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* 팝업 블록 모달 */}
      <PopupBlockedModal
        isOpen={isPopupBlocked}
        onClose={() => setIsPopupBlocked(false)}
      />
    </div>
  );
};

export default CheckoutPage;
